import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  CircularProgress, 
  Card, 
  CardMedia, 
  IconButton 
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { useShop } from '../../context/ShopContext';
import { supabase } from '../../services/supabaseClient';
import { NotificationService } from '../../services/errorService';

const ShopSettings: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const { shops, refreshShops } = useShop();
  const [shop, setShop] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const currentShop = shops.find(s => s.id === shopId);
    if (currentShop) {
      setShop(currentShop);
    } else if (shops.length > 0) {
      // If shop not found, maybe redirect or show error
      navigate('/dashboard/my-shops');
    }
    setLoading(false);
  }, [shopId, shops, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setShop((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'banner') => {
    if (!e.target.files || e.target.files.length === 0 || !shop) return;
    const file = e.target.files[0];
    const fileName = `${shop.id}/${type}_${Date.now()}`;
    
    const { data, error } = await supabase.storage
      .from('shop-assets')
      .upload(fileName, file);

    if (error) {
      NotificationService.error('Upload Failed', error.message);
      return;
    }

    const { data: { publicUrl } } = supabase.storage.from('shop-assets').getPublicUrl(fileName);
    const fieldName = type === 'logo' ? 'logo_url' : 'banner_url';
    setShop((prev: any) => ({ ...prev, [fieldName]: publicUrl }));
  };

  const handleSaveChanges = async () => {
    if (!shop) return;
    setSaving(true);
    const { error } = await supabase
      .from('shops')
      .update({ 
        name: shop.name, 
        description: shop.description, 
        slug: shop.slug, 
        logo_url: shop.logo_url, 
        banner_url: shop.banner_url 
      })
      .eq('id', shop.id);

    if (error) {
      NotificationService.error('Save Failed', error.message);
    } else {
      NotificationService.success('Shop Updated', 'Your shop details have been saved.');
      await refreshShops();
    }
    setSaving(false);
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!shop) {
    return <Typography>Shop not found.</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Shop Settings: {shop.name}</Typography>
      <Paper sx={{ p: 3, borderRadius: '16px' }}>
        <Grid container spacing={4}>
          {/* Left side - Form */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Shop Details</Typography>
            <TextField
              fullWidth
              label="Shop Name"
              name="name"
              value={shop.name || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="URL Slug"
              name="slug"
              value={shop.slug || ''}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              helperText="Unique identifier for your shop URL."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              name="description"
              value={shop.description || ''}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
          </Grid>

          {/* Right side - Image Uploads */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Branding</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">Logo</Typography>
                <Card sx={{ position: 'relative', height: 160 }}>
                  <CardMedia
                    component="img"
                    height="160"
                    image={shop.logo_url || 'https://images.unsplash.com/photo-1595855799321-4d10d0f77b78?auto=format&fit=crop&w=160'}
                    alt="Shop Logo"
                  />
                  <IconButton 
                    component="label" 
                    sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <PhotoCamera sx={{ color: 'white' }} />
                    <input type="file" hidden onChange={(e) => handleFileChange(e, 'logo')} accept="image/*" />
                  </IconButton>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">Banner</Typography>
                <Card sx={{ position: 'relative', height: 160 }}>
                  <CardMedia
                    component="img"
                    height="160"
                    image={shop.banner_url || 'https://images.unsplash.com/photo-1500673922987-e212871fec22?auto=format&fit=crop&w=800'}
                    alt="Shop Banner"
                  />
                  <IconButton 
                    component="label" 
                    sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <PhotoCamera sx={{ color: 'white' }} />
                    <input type="file" hidden onChange={(e) => handleFileChange(e, 'banner')} accept="image/*" />
                  </IconButton>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/dashboard/my-shops')} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveChanges} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ShopSettings;
